/**
 * Connect an agent the way a person thinks of it (ADR 0108 amendment): the agent tiles, the
 * machine chips, the sign-in on a machine shown inline (link and code, the paste field, progress
 * until Signed in), and the Connect dialog: how to connect, only what that needs, then the
 * sign-in itself.
 */
import { Check, Cloud, ExternalLink, KeyRound, LogIn, Plus } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { LAUNCH_ACCOUNT_AGENTS, LAUNCH_ACCOUNT_AGENT_LABEL, LAUNCH_ACCOUNT_SECRET_LABEL, launchAccountAgent, launchAccountSecretNeed, machineSignInAccount } from '@orgus/protocol';
import type { LaunchAccount, LaunchAccountAgent, LaunchAccountKind, LaunchAccountsState } from '@orgus/protocol';
import { BUTTON_VARIANT, Button, CopyButton, Dialog, ErrorState, Input, Pressable, RowStatus, Select, cn, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useMachines } from '../../lib/store.ts';
import { AgentGlyph } from '../adapters/AgentKind.tsx';
import { useCan } from '../auth/useMe.ts';
import { closeTerminal, useTerms } from '../terminal/api.ts';
import { TerminalView } from '../terminal/TerminalView.tsx';
import { CLOUD_LABEL, CONNECT_VENDOR, READINESS_LABEL, agentTile, connectWays } from './connect.ts';
import type { ConnectWay, MachineChip, MachineReadiness } from './connect.ts';
import { machineSignInOf, saveLaunchAccount, sendMachineSignInCode, signInMachine, startMachineSignIn, startSignIn, stopMachineSignIn, useLaunchAccounts } from './data.ts';
import { KEY_SOURCE, KindFields, draftError, emptyDraft, settingsOf } from './fields.tsx';
import type { Draft } from './fields.tsx';

// ── Machine chips ────────────────────────────────────────────────────────────

const DOT: Record<MachineReadiness, string> = {
  ready: 'bg-fg-subtle',
  'needs-sign-in': 'bg-status-attention',
  'signing-in': 'bg-status-working',
  offline: 'border border-fg-subtle',
  unknown: 'border border-fg-subtle',
};

function Dot({ state }: { state: MachineReadiness }) {
  return <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT[state])} />;
}

/** Each machine as a dot (grey ready, amber needs sign-in, cyan signing in, hollow offline), the names in the label. */
export function MachineDots({ chips, max = 6, className }: { chips: MachineChip[]; max?: number; className?: string }) {
  if (!chips.length) return null;
  const shown = chips.slice(0, max);
  const more = chips.length - shown.length;
  return (
    <span className={cn('flex items-center gap-1', className)} role="img" aria-label={chips.map((c) => `${c.name}: ${READINESS_LABEL[c.state]}`).join(', ')}>
      {shown.map((c) => (
        <Dot key={c.machineId} state={c.state} />
      ))}
      {more > 0 && <span className="text-caption text-fg-subtle num">+{more}</span>}
    </span>
  );
}

/** Each machine as a chip with its state; one that needs a sign-in starts it when onPick is given. */
export function MachineChips({ chips, onPick, max = 6, className }: { chips: MachineChip[]; onPick?: (c: MachineChip) => void; max?: number; className?: string }) {
  if (!chips.length) return null;
  const shown = chips.slice(0, max);
  const more = chips.length - shown.length;
  return (
    <ul className={cn('flex flex-wrap items-center gap-2', className)} aria-label="Machines">
      {shown.map((c) => {
        const body = (
          <>
            <Dot state={c.state} />
            <span className="truncate">{c.name}</span>
          </>
        );
        const pick = onPick && c.state === 'needs-sign-in';
        return (
          <li key={c.machineId} className="min-w-0">
            {pick ? (
              <Pressable
                className="inline-flex h-6 w-auto max-w-44 items-center gap-2 rounded-full bg-hover px-2 text-caption text-fg-muted hover:text-fg"
                title={`${READINESS_LABEL[c.state]}: sign in on ${c.name}`}
                aria-label={`${c.name}: ${READINESS_LABEL[c.state]}. Sign in`}
                onClick={() => onPick(c)}
              >
                {body}
              </Pressable>
            ) : (
              <span className="inline-flex h-6 max-w-44 items-center gap-2 rounded-full bg-hover px-2 text-caption text-fg-subtle" title={READINESS_LABEL[c.state]} aria-label={`${c.name}: ${READINESS_LABEL[c.state]}`}>
                {body}
              </span>
            )}
          </li>
        );
      })}
      {more > 0 && <li className="text-caption text-fg-subtle num">+{more}</li>}
    </ul>
  );
}

// ── The sign-in on one machine ───────────────────────────────────────────────

const STEPS = ['Start', 'Approve', 'Signed in'] as const;

/** Three dots on a line: where the sign-in is. */
function Track({ at }: { at: 0 | 1 | 2 }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Sign-in progress">
      {STEPS.map((label, i) => (
        <li key={label} className="flex items-center gap-2" aria-current={i === at ? 'step' : undefined}>
          {i > 0 && <span aria-hidden className={cn('h-px w-6', i <= at ? 'bg-accent' : 'bg-border')} />}
          <span aria-hidden className={cn('flex size-4 items-center justify-center rounded-full', i < at || at === 2 ? 'bg-accent text-accent-fg' : i === at ? 'border border-accent' : 'border border-border-strong')}>
            {(i < at || at === 2) && <Check className="size-3" />}
          </span>
          <span className={cn('text-caption', i === at ? 'text-fg' : 'text-fg-subtle')}>{label}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * A ChatGPT or Google sign-in on one machine, live from the hub's state: start it, then open
 * the link and enter the code (ChatGPT) or paste Google's code back, until Signed in. `auto`
 * starts it at once (the Connect dialog); otherwise it waits for Sign in.
 */
export function MachineSignIn({ state, a, machineId, machineName, signedIn, auto = false, onStarted }: { state: LaunchAccountsState | null; a: LaunchAccount; machineId: string; machineName: string; signedIn: boolean; auto?: boolean; onStarted?: () => void }) {
  const s = state ? machineSignInOf(state, a.id, machineId) : null;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'start' | 'code' | 'stop' | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>, what: string) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(toUiError(e, { what }));
    } finally {
      setBusy(null);
    }
  };
  const start = () =>
    run(
      'start',
      async () => {
        await startMachineSignIn(a.id, machineId);
        onStarted?.();
      },
      'The sign-in did not start.',
    );
  const [autoed, setAutoed] = useState(false);
  useEffect(() => {
    if (!auto || autoed || signedIn) return;
    setAutoed(true);
    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, autoed, signedIn]);
  const codex = launchAccountAgent(a) === 'codex';
  const done = signedIn || s?.step === 'done';
  const running = !!s && s.step !== 'done' && s.step !== 'failed';

  if (done)
    return (
      <div className="flex flex-col gap-3">
        <Track at={2} />
        <RowStatus tone="neutral" icon="done" label={`Signed in on ${machineName}`} />
      </div>
    );
  if (s?.step === 'failed' || (error && !running))
    return (
      <ErrorState
        variant="inline"
        error={
          error ?? {
            what: `The sign-in on ${machineName} did not finish.`,
            why: s?.error ?? 'The machine stopped it.',
            next: 'Try again when you are ready.',
          }
        }
        onRetry={start}
      />
    );
  if (!s)
    return (
      <Button variant="primary" icon={<LogIn />} loading={busy === 'start' || (auto && !error)} className="self-start" onClick={() => void start()}>
        Sign in on {machineName}
      </Button>
    );
  const at = s.step === 'starting' && !s.url ? 0 : 1;
  const linkClass = cn(BUTTON_VARIANT.secondary, 'self-start');
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Track at={at} />
      {s.step === 'starting' && <RowStatus tone="working" icon="working" label={s.url ? 'Checking the code' : `Starting on ${machineName}`} />}
      {s.step === 'open' && (
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-ui text-fg-muted">Open the sign-in page and enter this code.</p>
          <div className="flex items-center gap-3">
            <code className="rounded-control bg-hover px-3 py-2 font-mono text-title font-medium text-fg">{s.code}</code>
            {s.code && <CopyButton text={s.code} label="Copy code" />}
          </div>
          {s.url && (
            <a className={linkClass} href={s.url} target="_blank" rel="noopener noreferrer nofollow">
              <ExternalLink className="size-4" aria-hidden />
              Open sign-in page
            </a>
          )}
          {codex && <p className="text-caption text-fg-subtle">Device code login must be on in ChatGPT, Settings, Security.</p>}
          <RowStatus tone="working" icon="working" label="Approve in your browser" />
        </div>
      )}
      {s.step === 'paste' && (
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-ui text-fg-muted">Sign in with Google, then paste the code it shows.</p>
          {s.url && (
            <a className={linkClass} href={s.url} target="_blank" rel="noopener noreferrer nofollow">
              <ExternalLink className="size-4" aria-hidden />
              Open Google sign-in
            </a>
          )}
          <form
            className="flex min-w-0 items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.trim())
                void run(
                  'code',
                  async () => {
                    await sendMachineSignInCode(a.id, machineId, code.trim());
                    setCode('');
                  },
                  'The code was not sent.',
                );
            }}
          >
            <Input label="Code from Google" wrapperClassName="min-w-0 flex-1" value={code} onChange={(e) => setCode(e.target.value)} placeholder="4/0A..." spellCheck={false} autoComplete="off" className="font-mono" />
            <Button variant="primary" type="submit" loading={busy === 'code'} disabled={!code.trim()}>
              Send code
            </Button>
          </form>
        </div>
      )}
      {error && <ErrorState variant="inline" error={error} />}
      {running && (
        <Button size="sm" variant="ghost" className="self-start text-fg-subtle" loading={busy === 'stop'} onClick={() => void run('stop', () => stopMachineSignIn(a.id, machineId), 'The sign-in was not stopped.')}>
          Cancel sign-in
        </Button>
      )}
    </div>
  );
}

/** A Claude subscription's sign-in: claude setup-token in a terminal on one machine, inline, until the hub holds the token. */
function ClaudeSignIn({ a, machineId, machineName }: { a: LaunchAccount; machineId: string; machineName: string }) {
  const { terms } = useTerms();
  const [termId, setTermId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setTermId(await startSignIn(a.id, machineId));
    } catch (e) {
      setError(toUiError(e, { what: 'The sign-in did not start.' }));
    } finally {
      setBusy(false);
    }
  };
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (started || a.secret) return;
    setStarted(true);
    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, a.secret]);
  useEffect(() => {
    // Signed in: the terminal has done its job.
    if (a.secret && termId) {
      const t = terms.find((x) => x.id === termId);
      if (t && t.state !== 'exited') void closeTerminal(termId).catch(() => {});
    }
  }, [a.secret, termId, terms]);
  if (a.secret)
    return (
      <div className="flex flex-col gap-3">
        <Track at={2} />
        <RowStatus tone="neutral" icon="done" label="Signed in on every machine" />
      </div>
    );
  if (error) return <ErrorState variant="inline" error={error} onRetry={start} />;
  const term = termId ? terms.find((t) => t.id === termId) : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Track at={term ? 1 : 0} />
      <p className="text-ui text-fg-muted">Approve in your browser, then paste the code it shows into the terminal on {machineName}.</p>
      {term ? <TerminalView termId={term.id} compact className="w-full" style={{ height: 'min(16rem, 40dvh)' }} /> : <RowStatus tone="working" icon="working" label={`Starting on ${machineName}`} />}
    </div>
  );
}

// ── Connect ──────────────────────────────────────────────────────────────────

type Step = 'agent' | 'way' | 'sign-in';

/** A choice as a card: an icon, a bright label and a dim hint. */
function Choice({ icon, label, hint, selected, disabled, disabledReason, onSelect }: { icon: ReactNode; label: string; hint: string; selected: boolean; disabled?: boolean; disabledReason?: string; onSelect: () => void }) {
  return (
    <Pressable
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      onClick={onSelect}
      className={cn('flex items-center gap-3 rounded-card border px-3 py-3', selected ? 'border-accent bg-accent-soft' : 'border-border hover:bg-hover')}
    >
      <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-control bg-hover text-fg-muted">
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-ui font-medium text-fg">{label}</span>
        <span className="truncate text-caption text-fg-subtle">{disabled && disabledReason ? disabledReason : hint}</span>
      </span>
      {selected && <Check aria-hidden className="size-4 shrink-0 text-accent-text" />}
    </Pressable>
  );
}

/**
 * Connect an account, one short step per choice: the agent (skipped when a tile opened it), how
 * it connects (the usual way preselected), only what that needs, then the sign-in inline until
 * Signed in. A key is for an owner or admin: the choice says so when the person is neither.
 */
export function ConnectDialog({ open, agent: initial, onOpenChange, onOpenAccount }: { open: boolean; agent: LaunchAccountAgent | null; onOpenChange: (o: boolean) => void; onOpenAccount: (id: string) => void }) {
  const machines = useMachines();
  const { state } = useLaunchAccounts();
  const admin = useCan('admin');
  const [step, setStep] = useState<Step>('agent');
  const [d, setD] = useState<Draft>(emptyDraft());
  const [way, setWay] = useState<ConnectWay>('subscription');
  const [machineId, setMachineId] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => {
    if (!open) return;
    setStep(initial ? 'way' : 'agent');
    setD(emptyDraft(initial ?? 'claude-code'));
    setWay('subscription');
    setMachineId('');
    setAccountId(null);
    setError(null);
    setBusy(false);
  }, [open, initial]);
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  const ways = connectWays(d.agent);
  const chosen = ways.find((w) => w.way === way) ?? ways[0]!;
  const online = machines.filter((m) => m.online);
  const onMachine = machineId || signInMachine(machines) || '';
  const machineName = machines.find((m) => m.id === onMachine)?.name ?? 'the machine';
  const subscription = way === 'subscription';
  const account = accountId ? (state?.accounts.find((a) => a.id === accountId) ?? null) : null;

  const pickAgent = (g: LaunchAccountAgent) => {
    setD(emptyDraft(g));
    setWay('subscription');
    setStep('way');
  };
  const pickWay = (w: ConnectWay) => {
    const kinds = ways.find((x) => x.way === w)?.kinds ?? [];
    setWay(w);
    set({ kind: kinds[0] ?? 'subscription', secret: '' });
    setError(null);
  };

  const add = async () => {
    const problem = draftError(d, null);
    if (problem)
      return setError({
        what: 'The account was not added.',
        why: problem,
        next: 'Fix it and try again.',
      });
    setBusy(true);
    setError(null);
    try {
      const a = await saveLaunchAccount({
        agent: d.agent,
        kind: d.kind,
        settings: settingsOf(d),
        ...(d.secret.trim() ? { secret: d.secret.trim() } : {}),
      });
      if (subscription && onMachine) {
        setAccountId(a.id);
        setStep('sign-in');
      } else {
        onOpenChange(false);
        onOpenAccount(a.id);
      }
    } catch (e) {
      setError(toUiError(e, { what: 'The account was not added.' }));
    } finally {
      setBusy(false);
    }
  };

  const vendor = CONNECT_VENDOR[d.agent];
  const signedIn = !!account && (account.secret ? true : (state?.folders.some((f) => f.accountId === account.id && f.machineId === onMachine && f.state === 'signed-in' && account.folders[onMachine] === f.path) ?? false));
  const title = step === 'agent' ? 'Connect an agent' : `Connect ${vendor}`;
  const description = step === 'agent' ? 'Which agent should run on it' : step === 'way' ? `How ${LAUNCH_ACCOUNT_AGENT_LABEL[d.agent]} signs in` : signedIn ? undefined : `Signing in on ${machineName}`;
  const need = launchAccountSecretNeed(d.kind, d.agent);
  const primaryLabel = subscription ? (onMachine ? `Sign in on ${machineName}` : 'Add account') : way === 'api-key' ? 'Add key' : 'Add account';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={title}
      description={description}
      footer={
        step === 'way' ? (
          <>
            {!initial && (
              <Button variant="ghost" className="mr-auto" onClick={() => setStep('agent')} disabled={busy}>
                Back
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" icon={subscription && onMachine ? <LogIn /> : undefined} loading={busy} onClick={() => void add()}>
              {primaryLabel}
            </Button>
          </>
        ) : step === 'sign-in' ? (
          <>
            {account && (
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={() => {
                  onOpenChange(false);
                  onOpenAccount(account.id);
                }}
              >
                Open account
              </Button>
            )}
            <Button variant={signedIn ? 'primary' : 'ghost'} onClick={() => onOpenChange(false)}>
              {signedIn ? 'Close' : 'Finish later'}
            </Button>
          </>
        ) : undefined
      }
    >
      {step === 'agent' && (
        <div role="radiogroup" aria-label="Agent" className="flex flex-col gap-2">
          {LAUNCH_ACCOUNT_AGENTS.map((g) => (
            <Choice key={g} icon={<AgentGlyph kind={g} className="size-4" />} label={LAUNCH_ACCOUNT_AGENT_LABEL[g]} hint={`Connect ${CONNECT_VENDOR[g]}`} selected={false} onSelect={() => pickAgent(g)} />
          ))}
        </div>
      )}
      {step === 'way' && (
        <form
          className="flex flex-col gap-4"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <div role="radiogroup" aria-label="How it connects" className="flex flex-col gap-2">
            {ways.map((w) => {
              const keyOnly = w.way === 'api-key' && !admin;
              return (
                <Choice
                  key={w.way}
                  icon={w.way === 'subscription' ? <AgentGlyph kind={d.agent} className="size-4" /> : w.way === 'api-key' ? <KeyRound className="size-4" /> : <Cloud className="size-4" />}
                  label={w.label}
                  hint={w.hint}
                  selected={way === w.way}
                  disabled={keyOnly}
                  disabledReason="Only an owner or admin can add a key"
                  onSelect={() => pickWay(w.way)}
                />
              );
            })}
          </div>
          {way === 'cloud' && chosen.kinds.length > 1 && (
            <Select
              label="Provider"
              value={d.kind}
              onValueChange={(k) => set({ kind: k as LaunchAccountKind })}
              options={chosen.kinds.map((k) => ({
                value: k,
                label: CLOUD_LABEL[k] ?? k,
              }))}
            />
          )}
          {way === 'cloud' && <KindFields d={d} set={set} />}
          {need === 'required' && (
            <Input
              label={LAUNCH_ACCOUNT_SECRET_LABEL[d.kind]}
              type="password"
              autoComplete="new-password"
              value={d.secret}
              onChange={(e) => set({ secret: e.target.value })}
              hint={`From ${KEY_SOURCE[d.agent]}. Stored encrypted, never shown again.`}
              spellCheck={false}
              className="font-mono"
              autoFocus
            />
          )}
          {subscription && online.length > 1 && <Select label="Sign in on" value={onMachine} onValueChange={setMachineId} options={online.map((m) => ({ value: m.id, label: m.name }))} />}
          {subscription && !onMachine && <p className="text-caption text-fg-subtle">No machine is online</p>}
          {subscription && onMachine && <p className="text-caption text-fg-subtle">{d.agent === 'claude-code' ? 'One sign-in covers every machine.' : 'Each machine signs in once. Nothing leaves the machine.'}</p>}
          {error && <ErrorState variant="inline" error={error} />}
        </form>
      )}
      {step === 'sign-in' && account && (d.agent === 'claude-code' ? <ClaudeSignIn a={account} machineId={onMachine} machineName={machineName} /> : <MachineSignIn state={state} a={account} machineId={onMachine} machineName={machineName} signedIn={signedIn} auto />)}
      {step === 'sign-in' && !account && <RowStatus tone="working" icon="working" label="Adding the account" />}
    </Dialog>
  );
}

// ── The agent tiles ──────────────────────────────────────────────────────────

/**
 * One tile per agent: what it runs on, each machine's state as a chip, and one primary action
 * for its state (Connect, Sign in on <machine>, or Add another account, quiet).
 */
export function AgentTiles({ state, onConnect, onOpen }: { state: LaunchAccountsState; onConnect: (agent: LaunchAccountAgent) => void; onOpen: (id: string, machineId?: string) => void }) {
  const machines = useMachines();
  const write = useCan('operator');
  return (
    <ul className="grid gap-3 md:grid-cols-3" aria-label="Agents">
      {LAUNCH_ACCOUNT_AGENTS.map((g) => {
        const t = agentTile(state, g, machines);
        const a = t.action;
        return (
          <li key={g} className="flex min-w-0 flex-col gap-3 rounded-card bg-surface p-4">
            <div className="flex min-w-0 items-center gap-3">
              <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-control bg-hover">
                <AgentGlyph kind={g} className="size-4" />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-caption text-fg-subtle">{LAUNCH_ACCOUNT_AGENT_LABEL[g]}</span>
                {t.lead && !t.lead.builtIn ? (
                  <Pressable className="w-auto truncate rounded-control text-body font-medium text-fg hover:text-accent-text" onClick={() => onOpen(t.lead!.id)}>
                    {t.title}
                  </Pressable>
                ) : (
                  <span className={cn('truncate text-body font-medium', t.accounts.length || t.title !== 'Not connected' ? 'text-fg' : 'text-fg-muted')}>{t.title}</span>
                )}
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <MachineChips chips={t.chips} onPick={write && t.lead && !t.lead.builtIn && (machineSignInAccount(t.lead) || !t.lead.secret) ? (c) => onOpen(t.lead!.id, c.machineId) : undefined} />
              <span className="text-caption text-fg-subtle">{t.detail}</span>
            </div>
            <div className="mt-auto flex">
              {a.kind === 'connect' && (
                <Button variant="primary" size="sm" disabled={!write} title={write ? undefined : 'Only an owner, admin or operator can connect accounts'} onClick={() => onConnect(g)}>
                  Connect {CONNECT_VENDOR[g]}
                </Button>
              )}
              {a.kind === 'sign-in' && (
                <Button variant="secondary" size="sm" icon={<LogIn />} disabled={!write} onClick={() => onOpen(a.accountId, a.machineId)}>
                  Sign in on {a.machineName}
                </Button>
              )}
              {a.kind === 'add' && (
                <Button variant="ghost" size="sm" icon={<Plus />} className="text-fg-subtle" disabled={!write} onClick={() => onConnect(g)}>
                  Add another account
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
